/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'
import { useNavigate } from 'react-router-dom'

import { EuiEmptyPrompt, EuiFlexGroup, EuiImage, EuiLink, EuiText } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { resourceCreationManagedUrl } from '@modules/project-creation-lib/urls'

import emptyTableIllustration from './files/emptyTableIllustration.svg'

type Props = {
  hasFilters: boolean
}

export const ProjectsTableEmptyState: React.FunctionComponent<Props> = ({ hasFilters }) => {
  const navigate = useNavigate()

  const bodyContent = hasFilters ? (
    <EuiText css={{ minWidth: '600px' }}>
      <FormattedMessage
        id='cps.projectsTable.noResults.body'
        defaultMessage='Try using different search terms, or reset your filters to broaden the results.'
      />
    </EuiText>
  ) : (
    <EuiFlexGroup gutterSize='xs' direction='column' alignItems='center'>
      <EuiText>
        <FormattedMessage
          id='cps.projectsTable.noProjects.title'
          defaultMessage="You don't have any projects to link."
        />
      </EuiText>
      <EuiLink
        onClick={() => {
          navigate(resourceCreationManagedUrl())
        }}
        color='primary'
        data-test-subj='cps-projectsTable-createProjectButton'
      >
        <FormattedMessage
          id='cps.projectsTable.noProjects.createProjectButton'
          defaultMessage='Create project'
        />
      </EuiLink>
    </EuiFlexGroup>
  )

  return (
    <EuiEmptyPrompt
      icon={
        <EuiImage
          size='fullWidth'
          src={emptyTableIllustration}
          alt='Illustration for cross-project search'
        />
      }
      titleSize='s'
      title={
        <h2>
          <FormattedMessage
            id='cps.projectsTable.noResults.title'
            defaultMessage='No results found'
          />
        </h2>
      }
      body={bodyContent}
    />
  )
}
